import moment, { localMomentInUTC } from 'shared/lib/moment'
import intersection from 'lodash/intersection'
import difference from 'lodash/difference'
import some from 'lodash/some'
import OperatingRulesUtils from 'shared/utils/operating_rules'
import TimeUtils from 'shared/utils/time'
import SettingUtils from 'shared/utils/settings'

const dayTypes = {
  weekday: [1, 2, 3, 4, 5],
  weekend: [0, 6],
  mon: [1],
  tue: [2],
  wed: [3],
  thu: [4],
  fri: [5],
  sat: [6],
  sun: [0]
}

export const getDateForTimeString = (date, timestring) => {
  return moment(date).set({
    hour: timestring.split(':')[0],
    minute: timestring.split(':')[1]
  })
}

export const getDateTimesForTimeslot = (date, timeslot) => {
  const key = Object.keys(timeslot.data)[0]
  const from = timeslot.data[key].from
  const till = timeslot.data[key].till

  return [
    getDateForTimeString(date, from),
    getDateForTimeString(date, till)
  ]
}

export const getDefaultTime = ({ type, date, openingHours, defaultStartTime, forceDefaultTime = false, allowPast }) => {
  const useOpeningHours = !!openingHours
  const dateAsMoment = moment(date)
  const now = localMomentInUTC(new Date())

  // Base default time on now
  dateAsMoment.set({ hour: now.hour(), minute: now.minute() })

  let defaultTime = TimeUtils.defaultTime(type, {
    date: dateAsMoment,
    useOpeningHours,
    operatingRules: openingHours,
    skipTimeslots: true,
    defaultStartTime,
    dontChangeDate: type === 'start',
    allowPast
  })

  if (type === 'start') {
    if (defaultTime.day() !== dateAsMoment.day() && !forceDefaultTime) {
      // Default times select a different day, this could be because of relative settings
      // Revert back to the selected day
      defaultTime = dateAsMoment.set({ hour: defaultTime.hour(), minute: defaultTime.minute() })
    }
  }

  return defaultTime
}

export const getClosedDays = ({ openingHours }) => {
  const rules = []

  // Days to exclude
  let closedDays = [0, 1, 2, 3, 4, 5, 6]

  openingHours.forEach((rule) => {
    switch (rule.data_type) {
      case 'hours': {
        const days = dayTypes[Object.keys(rule.data)[0]]

        // Open on these days, remove from closed
        closedDays = difference(closedDays, days)
        break
      }
      case 'away': {
        const data = rule.data.away
        const fromMoment = moment(data.from)
        const tillMoment = moment(data.till)
        const awayRange = moment.range(fromMoment, tillMoment)

        // Find and return all days that are completely covered by this away period
        let currentDay = fromMoment.clone().startOf('day')
        const endDay = tillMoment.clone().startOf('day')

        while (currentDay.isSameOrBefore(endDay, 'day')) {
          const dayStart = currentDay.clone().startOf('day')
          const dayEnd = currentDay.clone().endOf('day')

          if (awayRange.contains(dayStart) && awayRange.contains(dayEnd)) {
            rules.push({ from: dayStart.toDate(), to: dayEnd.toDate() })
          }

          currentDay.add(1, 'day')
        }
      }
    }
  })

  // Some days were removed so we have open days
  if (closedDays.length < 7) {
    rules.push({ daysOfWeek: closedDays })
  }

  return rules
}

export const isDayClosed = ({ date, openingHours, allowPast = true }) => {
  const closedDays = openingHours ? getClosedDays({ openingHours }) : []

  if (moment(date).isBefore(localMomentInUTC(), 'day') && !allowPast) return true

  if (closedDays.length === 0) return false

  return some(closedDays, (object) => {
    if (object.daysOfWeek) {
      return object.daysOfWeek.indexOf(moment(date).day()) >= 0
    } else if (object.from) {
      return moment(date).isBetween(moment(object.from), moment(object.to), 'day', '[]')
    }
  })
}

export const isDaySelectable = (options) => {
  const { type, date, openingHours, values, durationData, minDate, allowPast, defaultTimes } = options

  if (isDayClosed({ date, openingHours })) {
    return false
  }

  if (openingHours && !allowPast) {
    if (moment(date).isBefore(localMomentInUTC(), 'day')) return false
  }

  if (minDate) {
    if (moment(date).isBefore(moment(minDate), 'day')) return false
  }

  if (type === 'start') {
    if (durationData) {
      const { duration, period } = durationData
      const startTime = moment(date)

      const stopTime = calculateStopTimeForFixedDuration({ startTime, duration, period, defaultTimes })

      // Disallow selecting a start date if the added duration is ending on an unavailable date
      if (!isDaySelectable({
        ...options,
        type: 'predictive-stop',
        date: stopTime
      })) {
        return false
      }
    }
  }

  if (type === 'stop') {
    // Disallow selecting a stop date before the start date
    if (moment(date).isBefore(moment(values.starts_at), 'day')) return false
  }

  return true
}

export const isTimeSelectable = ({ type, timestring, openingHours, values, durationData, allowPast }) => {
  const date = type === 'start' ? values.starts_at : values.stops_at

  let dateTime

  if (timestring) {
    dateTime = getDateForTimeString(date, timestring)
  } else {
    dateTime = moment(date)
  }

  if (!date || !dateTime.isValid()) return true

  if (openingHours) {
    if (!OperatingRulesUtils.isTimeSelectable(dateTime, {
      operatingRules: openingHours,
      skipTimeslots: true,
      allowPast
    })) {
      return false
    }

    // Duration data provided for start time
    // Check if this start time is possible
    if (type === 'start' && durationData) {
      const stopDate = moment(dateTime).add(durationData.length, 'seconds')

      // Duration is hourly and less than a day so return time is set based on start time
      // Time is not selectable if return time would be unavailable
      if (
        durationData.period === 'hour' &&
        durationData.length <= 86400 &&
        !OperatingRulesUtils.isTimeSelectable(stopDate, {
          operatingRules: openingHours,
          skipTimeslots: true,
          allowPast
        })
      ) {
        return false
      }
    }
  }

  if (type === 'stop') {
    // Disallow selecting a stop time before the start time
    if (dateTime.isSame(moment(values.starts_at)) || dateTime.isBefore(moment(values.starts_at))) {
      return false
    }
  }

  return true
}

export const isLocationSelectable = ({ type, location, values, locations }) => {
  if (!locations) {
    return true
  }

  // No stop location selected yet, allow any location
  if (type === 'start' && !values.stop_location_id) {
    return true
  }

  // No start location selected yet, allow any location
  if (type === 'stop' && !values.start_location_id) {
    return true
  }

  // No locations selected yet, allow any location
  if (!values.start_location_id && !values.stop_location_id) {
    return true
  }

  location = location || locations.getById(type === 'start' ? values.start_location_id : values.stop_location_id)
  const otherLocation = locations.getById(type === 'start' ? values.stop_location_id : values.start_location_id)

  if (!location || !otherLocation) return true
  if (location.id === otherLocation.id) return true

  const attachedToCluster = intersection(location.cluster_ids, otherLocation.cluster_ids).length > 0

  return attachedToCluster
}

export const validate = ({ values, locations, openingHours, allowPast }) => {
  const errors = {}

  if (!values) {
    return {
      start_location_id: 'required',
      stop_location_id: 'required',
      starts_at: { date: 'required', time: 'required' },
      stops_at: { date: 'required', time: 'required' }
    }
  }

  if (!isLocationSelectable({ type: 'stop', values, locations })) {
    errors.stop_location_id = 'cannot be selected'
  }

  if (!isDaySelectable({ type: 'start', date: values.starts_at, values, openingHours, allowPast })) {
    errors.starts_at = { date: 'not available' }
  }

  if (!isDaySelectable({ type: 'stop', date: values.stops_at, values, openingHours, allowPast })) {
    errors.stops_at = { date: 'not available' }
  }

  if (!isTimeSelectable({ type: 'start', values, openingHours, allowPast })) {
    errors.starts_at = errors.starts_at || {}
    errors.starts_at.time = 'not available'
  }

  if (!isTimeSelectable({ type: 'stop', values, openingHours, allowPast })) {
    errors.stops_at = errors.stops_at || {}
    errors.stops_at.time = 'not available'
  }

  return errors
}

/**
 * Checks if a location input is enabled according to the behavior from the settings.
 *
 * Type
 *   'both': Checks if any location input is allowed. If not, the location is set automatically.
 *   'start': Checks if a start location input is allowed.
 *   'stop': Checks if a stop location input is allowed.
 *
 * Defaults to not disallow on invalid input.
 */
export const isLocationInputEnabled = (type, options = {}) => {
  const locationBehavior = SettingUtils.get('store.behaviors.location_picker', 'start_stop')

  // The order screen can always show all location inputs.
  if (options.scope && options.scope === 'order') return true

  switch (type) {
    case 'both': return locationBehavior !== 'auto'
    case 'start': return ['start_stop', 'start'].includes(locationBehavior)
    case 'stop': return locationBehavior === 'start_stop'
    default: return false
  }
}

/**
 * Calculate the stop time given the start time and duration.
 *
 * @param {Object} options
 * @param {moment} options.startTime - The start time
 * @param {String} options.period - The period of the duration (hour, day, week...)
 * @param {Number} options.duration - The length of the duration in units defined in "period"
 * @param {Object} options.defaultTimes - The default start and stop times
 * @returns {
 *  moment
 * }
 */
export const calculateStopTimeForFixedDuration = ({ startTime, duration, period, defaultTimes }) => {
  if (!(startTime && moment.isMoment(startTime))) {
    throw new Error('startTime must be a momentjs object')
  }

  const stopTime = startTime.clone().add(duration, period)

  // If duration is set to day and it's more than 24 hours, we need to subtract one day
  // This is needed because by our business rules 2 days duration means "one midnight passed" and not "48 hours passed".
  const periodIsLongerThan24Hours = moment.duration(duration, period).asSeconds() >= 86400
  if (period === 'day' && periodIsLongerThan24Hours) {
    stopTime.subtract(1, 'day')
  }

  // If the start time is after the end time, we need to add one day to the end time.
  // For example if period is 1 day, start time is at 14:00 and return time is at 10:00
  // If we pick 2024-01-01 14:00, the return time should be 2024-01-02 10:00
  // We return one day later than usual.
  const stopTimeIsBeforeStartTime = defaultTimes.stop.hour <= defaultTimes.start.hour && defaultTimes.stop.minute <= defaultTimes.start.minute
  if (period === 'day' && stopTimeIsBeforeStartTime) {
    stopTime.add(1, 'day')
  }

  // Set to default stop time, except when using hours period.
  // If hour is used, we have exact time when it needs to end (X hours from the start) so default time is not needed.
  if (period !== 'hour') {
    stopTime.set({ h: defaultTimes.stop.hour, m: defaultTimes.stop.minute })
  }

  return stopTime
}

export const defaultDefaultTimes = {
  start: '09:00',
  stop: '15:00'
}
